import {
    AccountWithExtraInfo,
    AgreementCode,
    AgreementEntry,
    User,
    UserType,
    UserWithExtraInfo,
} from '@orbis-systems/orbis-api-middleware';
import { createQueryFn } from '@orbis-systems/orbis-react-toolkit';
import { api } from './baseApi';
import { DataTags } from './dataTags';

export const userApi = api.injectEndpoints({
    endpoints: (build) => ({
        getUserAgreements: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) => api.orbisMiddleware.user.getUserAgreements()),
            providesTags: [DataTags.Agreements],
        }),
        signUserAgreement: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        code,
                        entries,
                    }: {
                        code: AgreementCode;
                        entries: AgreementEntry[];
                    },
                    { extra: api },
                ) => api.orbisMiddleware.user.signUserAgreement(code, entries),
            ),
            invalidatesTags: [DataTags.Agreements],
        }),
        getUsers: build.query({
            queryFn: createQueryFn(
                async (userTypeFilter: UserType | UserType[] | undefined, { extra: api }): Promise<User[]> =>
                    api.orbisMiddleware.branch.getUsers(userTypeFilter),
            ),
        }),
        getUser: build.query({
            queryFn: createQueryFn(
                async (userId: number, { extra: api }): Promise<UserWithExtraInfo | null> =>
                    api.orbisMiddleware.user.getUser(userId),
            ),
        }),
        getUserAccounts: build.query({
            queryFn: createQueryFn(
                async (userId: number, { extra: api }): Promise<AccountWithExtraInfo[]> =>
                    api.orbisMiddleware.user.getAccounts(userId),
            ),
        }),
        getUserAccountTypesBreakdown: build.query({
            queryFn: createQueryFn(async (userId: number, { extra: api }) => {
                const data = await api.orbisMiddleware.stat.getUserAccountTypesBreakdownStat(userId);
                return data;
            }),
        }),
        uploadUserAvatar: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        file,
                        userId,
                    }: {
                        file: File;
                        userId: number;
                    },
                    { extra: api },
                ) =>
                    api.middleware.avatar.uploadUserAvatar(userId, {
                        image: file,
                    }),
                {
                    pendingMessage: 'Changing Avatar…',
                    alertIfError: true,
                },
            ),
        }),
        removeUserAvatar: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        userId,
                    }: {
                        userId: number;
                    },
                    { extra: api },
                ) => {
                    await api.middlewareSdk.delete('avatars/users/:userId', undefined, {
                        pathParams: { userId },
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    });

                    return userId;
                },
                {
                    pendingMessage: 'Removing Avatar…',
                    alertIfError: true,
                },
            ),
        }),
    }),
});

export const {
    useGetUsersQuery,
    useGetUserQuery,
    useGetUserAccountsQuery,
    useGetUserAccountTypesBreakdownQuery,
    useUploadUserAvatarMutation,
    useRemoveUserAvatarMutation,
    useGetUserAgreementsQuery,
    useSignUserAgreementMutation,
    useLazyGetUserQuery,
} = userApi;
